import Idea from "@/types/Idea"
import IdeaProperty from "@/types/IdeaProperty"
import Instance from "@/types/Instance"
import {asArray} from "@/utils/arrayUtils"
import {instanceMediaPath, isPending, MediaRef, revoke, takePending, upload} from "@/services/mediaStorage"

/** Metadados que o servidor manda por propriedade reversa (InstanceService.reverseInfo) */
export type ReverseInfo = {
    idea: string
    reverse: string
    multiple: boolean
    unique: boolean
    ownerMultiple: boolean
    ownerRequired: boolean
    /** o campo dono faz parte da chave do outro lado: reverso vira somente leitura */
    ownerIdentifier: boolean
    ownerName: string
    ownerIdeaName: string
}

export type ReverseInfos = { [propertyAlias: string]: ReverseInfo }

export type DomainItem = {
    _instance: number
    _identifier: string
    _owners?: { [ownerField: string]: any }
    /** identificador sem a parte da chave que aponta de volta, por campo dono */
    _identifiers?: { [ownerField: string]: string }
}

export function isReverse(property: IdeaProperty): boolean {
    return !!(property.relationship && property.relationship.reverse)
}

/**
 * O alias plano de uma propriedade reversa vem undefined: os valores moram em
 * instance._rels[alias]. Semeia a partir de lá, sempre como array de ids.
 * Precisa rodar ANTES do deepCopy que vira originalInstance, senão todo campo
 * reverso parece sujo e o botão salvar acende sozinho.
 *
 * **Sobrescreve o que estiver no alias, sempre.** O `_rels` é a ÚNICA verdade de um campo
 * reverso — o valor mora nos documentos do outro lado —, então nada que esteja no alias
 * plano tem autoridade sobre ele. Antes havia uma guarda por `undefined`, e ela abria dois
 * buracos com o mesmo sintoma: o campo aparecia VAZIO num item que tinha vínculos.
 *
 * 1. Documento antigo com o alias reverso gravado como `null` — 112 deles, de quando o
 *    servidor ainda não separava as alterações reversas das próprias. `null !== undefined`,
 *    a guarda barrava a semeadura, e o campo abria vazio.
 * 2. Alias reverso gravado como array desatualizado, que discordava do `_rels`.
 *
 * E o campo vazio não era só cosmético: ao salvar, o cliente mandava a lista sem os
 * vínculos que não apareceram, e o servidor lia isso como REMOÇÃO — recusando com
 * "faz parte da chave" quando o campo dono compõe o identificador do outro lado.
 *
 * Os dois chamadores — a página de editar e o modal — carregam a instância com
 * `meta: ["rels", ...]`, então `_rels` está sempre presente; e quem já hidratou (o
 * ListInstances) chega ao mesmo valor, da mesma fonte, de modo que reescrever não muda nada.
 */
export function seedReverseValues(instance: Instance, idea: Idea): Instance {
    if (!instance || !idea) return instance
    idea.properties.filter(isReverse).forEach(property => {
        const rels = instance._rels && instance._rels[property.alias]
        instance[property.alias] = asArray(rels).map((rel: any) => rel._instance)
    })
    return instance
}

/**
 * O item do lado de lá pode vir para esta lista? Não quando já é de OUTRO e o campo dono de lá é de um
 * valor só (pedido do usuário: não se rouba — obrigatório ou opcional): vir para cá o tiraria de lá, o que
 * o outro não pode fazer pela lista dele. Muda-se pelo próprio item, no campo dele. No campo de lá de
 * vários valores, vir para cá só acrescenta, e pode; o que já é deste (`selfId`), também.
 *
 * Era o "Mover itens?", que perguntava e roubava. Um dono para as duas listas que oferecem itens de lá —
 * o campo reverso do formulário e o "Adicionar" da seção na ficha —, que não oferecem o que não pode; o
 * servidor recusa por fora (`reverse-taken`). O item vem do candidato (`_owners`) ou inteiro (o campo).
 */
export function freeToLink(item: DomainItem, info: ReverseInfo, selfId: number | null): boolean {
    if (info.ownerMultiple) return true
    const stored = item._owners ? item._owners[info.reverse] : (item as Record<string, unknown>)[info.reverse]
    const owner = asArray(stored as number | number[] | null | undefined)[0]
    return owner === null || owner === undefined || (selfId !== null && Number(owner) === Number(selfId))
}

/** Instância do outro lado de um reverso, montada antes de o dono existir */
export type PendingReverse = {
    ideaAlias: string
    ownerField: string
    ownerMultiple: boolean
    data: Record<string, any>
    /** propriedades de mídia da OUTRA coleção, que só o dialog dela conhece */
    mediaAliases?: MediaRef[]
}

/**
 * Ids provisórios (negativos) representam instâncias que ainda não existem no
 * banco. Não podem ir no POST do dono — elas nascem depois, na segunda etapa.
 */
export function withoutPendingIds(plain: Record<string, any>, idea: Idea): Record<string, any> {
    const result = {...plain}
    idea.properties.filter(isReverse).forEach(property => {
        const value = result[property.alias]
        if (Array.isArray(value)) result[property.alias] = value.filter(id => Number(id) >= 0)
    })
    return result
}

type PendingInstanceApi = {
    add: (params: { systemAlias: string, ideaAlias: string }, instance: any) => Promise<any>
    update: (params: { systemAlias: string, ideaAlias: string, instanceId: number }, changes: any) => Promise<any>
}

/** Segunda etapa: cria o que ficou pendente, já apontando para o dono recém-criado. */
export async function createPending(
    instances: PendingInstanceApi,
    systemAlias: string, ownerId: number, pending: PendingReverse[] = []
) {
    for (const item of pending) {
        const data = {
            ...item.data,
            [item.ownerField]: item.ownerMultiple ? [Number(ownerId)] : Number(ownerId)
        }

        //Mesma restrição do create: o caminho no bucket depende do id, que só existe
        //depois do POST. Sem isto o blob: seria persistido como valor.
        const media = (item.mediaAliases || []).filter(({alias}) => isPending(data[alias]))
        media.forEach(({alias}) => {data[alias] = null})

        const added = await instances.add({systemAlias, ideaAlias: item.ideaAlias}, data)
        if (!media.length) continue

        const instanceId = Number(added._instance)
        const applied: Record<string, string> = {}
        for (const {alias, type} of media) {
            const blob = takePending(item.data[alias])
            if (!blob) continue
            applied[alias] = await upload(
                instanceMediaPath(systemAlias, item.ideaAlias, instanceId, alias, type), blob)
        }
        if (!Object.keys(applied).length) continue

        await instances.update({systemAlias, ideaAlias: item.ideaAlias, instanceId}, applied)
        Object.keys(applied).forEach(alias => revoke(item.data[alias]))
    }
}

export default {isReverse, seedReverseValues, freeToLink, withoutPendingIds, createPending}
